import { useState } from 'react'
import type { Deal, PipelineStage, Contact } from '../../types'
import { STAGE_LABELS, PIPELINE_STAGES, CURRENCIES } from '../../types'
import { useDataStore } from '../../stores/dataStore'
import { formatCurrency, parseCurrencyInput, formatCurrencyInput } from '../../utils/currency'
import { Button, Input, Select, Modal, ConfirmModal } from '../ui'
import { toast } from '../ui/Toast'
import ActivityFeed from '../timeline/ActivityFeed'
import TaskList from '../timeline/TaskList'
import { ComposeFromDeal, CompanyEmailHistory } from '../gmail'

interface DealDetailPanelProps {
  deal: Deal
  isOpen: boolean
  onClose: () => void
}

export default function DealDetailPanel({
  deal,
  isOpen,
  onClose,
}: DealDetailPanelProps) {
  const { companies, contacts, updateDeal, deleteDeal } = useDataStore()
  const [isEditing, setIsEditing] = useState(false)
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false)

  const [title, setTitle] = useState(deal.title)
  const [value, setValue] = useState(formatCurrencyInput(deal.value))
  const [currency, setCurrency] = useState(deal.currency)
  const [stage, setStage] = useState(deal.stage)
  const [companyId, setCompanyId] = useState(deal.companyId)

  const company = companies.find((c) => c.id === deal.companyId)
  
  const allCompanyContacts = contacts.filter((c) => c.companyId === deal.companyId)
  const contactsWithEmail = allCompanyContacts.filter((c) => c.email)
  const primaryContact: Contact | undefined = contactsWithEmail[0]

  const handleSave = async () => {
    try {
      await updateDeal(deal.id, {
        title,
        value: parseCurrencyInput(value),
        currency,
        stage,
        companyId,
      })
      setIsEditing(false)
      toast('Deal updated', 'success')
    } catch {
      toast('Failed to update deal', 'error')
    }
  }

  const handleDelete = async () => {
    try {
      await deleteDeal(deal.id)
      toast('Deal deleted', 'info')
      onClose()
    } catch {
      toast('Failed to delete deal', 'error')
    }
  }

  const handleMarkWon = async () => {
    try {
      await updateDeal(deal.id, { stage: 'won' })
      toast('Deal marked as won', 'success')
      onClose()
    } catch {
      toast('Failed to update deal', 'error')
    }
  }

  const handleMarkLost = async () => {
    try {
      await updateDeal(deal.id, { stage: 'lost' })
      toast('Deal marked as lost', 'info')
      onClose()
    } catch {
      toast('Failed to update deal', 'error')
    }
  }

  const stageOptions = PIPELINE_STAGES.map((s) => ({
    value: s,
    label: STAGE_LABELS[s],
  }))

  const currencyOptions = CURRENCIES.map((c) => ({
    value: c,
    label: c,
  }))

  const companyOptions = [
    { value: '', label: 'No company' },
    ...companies.map((c) => ({ value: c.id, label: c.name })),
  ]

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={isEditing ? 'Edit deal' : 'Deal details'}
        footer={
          isEditing ? (
            <>
              <Button variant="ghost" onClick={() => setIsEditing(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSave}>
                Save changes
              </Button>
            </>
          ) : (
            <>
              <Button variant="destructive" onClick={() => setIsDeleteConfirmOpen(true)}>
                Delete
              </Button>
              <Button variant="secondary" onClick={() => setIsEditing(true)}>
                Edit
              </Button>
            </>
          )
        }
      >
        {isEditing ? (
          <div className="space-y-4">
            <Input
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Value"
                type="number"
                step="0.01"
                value={value}
                onChange={(e) => setValue(e.target.value)}
              />
              <Select
                label="Currency"
                options={currencyOptions}
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
              />
            </div>

            <Select
              label="Stage"
              options={stageOptions}
              value={stage}
              onChange={(e) => setStage(e.target.value as PipelineStage)}
            />

            <Select
              label="Company"
              options={companyOptions}
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value)}
            />
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <p className="text-xs text-text-secondary mb-1">Title</p>
              <p className="text-sm font-medium text-text-primary">{deal.title}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-text-secondary mb-1">Value</p>
                <p className="font-mono font-semibold text-action-primary">
                  {formatCurrency(deal.value, deal.currency)}
                </p>
              </div>
              <div>
                <p className="text-xs text-text-secondary mb-1">Stage</p>
                <p className="text-sm">{STAGE_LABELS[deal.stage]}</p>
              </div>
            </div>

            {company && (
              <div>
                <p className="text-xs text-text-secondary mb-1">Company</p>
                <p className="text-sm">{company.name}</p>
              </div>
            )}

            <div className="flex flex-wrap gap-2 pt-4 border-t border-border">
              <Button variant="primary" size="sm" onClick={handleMarkWon}>
                Mark won
              </Button>
              <Button variant="ghost" size="sm" onClick={handleMarkLost}>
                Mark lost
              </Button>
              {primaryContact && (
                <ComposeFromDeal
                  deal={deal}
                  contact={primaryContact}
                  variant="secondary"
                  size="sm"
                />
              )}
            </div>

            <div className="pt-4 border-t border-border">
              <TaskList entityType="deal" entityId={deal.id} />
            </div>

            <div className="pt-4 border-t border-border">
              <ActivityFeed entityType="deal" entityId={deal.id} />
            </div>

            {allCompanyContacts.length > 0 && (
              <div className="pt-4 border-t border-border">
                <CompanyEmailHistory contacts={allCompanyContacts} />
              </div>
            )}
          </div>
        )}
      </Modal>

      <ConfirmModal
        isOpen={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title="Delete deal"
        message="This will permanently delete this deal. This action cannot be undone."
        confirmText="Delete"
        variant="danger"
      />
    </>
  )
}
